/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router';

import { Grid, Link, Skeleton, Typography2, breakpoints } from '@coursera/cds-core';
import { HelpIcon } from '@coursera/cds-icons';

import type { Program } from 'bundles/enterprise-admin-skills/utils/programsLastVisited';
import { getProgramsFromLocalStorage } from 'bundles/enterprise-admin-skills/utils/programsLastVisited';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  wrapper: () => css`
    background-color: var(--cds-color-grey-25);
    border: 1px solid var(--cds-color-grey-50);
    border-radius: 3px;
    padding: var(--cds-spacing-150) 0;
    flex-direction: row;
    row-gap: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      flex-direction: column;
    }
  `,
  link: () => css`
    margin-left: var(--cds-spacing-600);

    ${breakpoints.down('md')} {
      margin-left: var(--cds-spacing-400);
    }
  `,
  icon: () => css`
    margin-left: var(--cds-spacing-300);
    margin-right: var(--cds-spacing-100);
  `,
};

type Props = {
  shouldHideProgramsBar: boolean;
  orgId: string;
  orgSlug: string;
};
const MAX_VISIBILE_PROGRAMS = 3;

const RecentlyVisitedLearningProgramsBar = ({ shouldHideProgramsBar, orgId, orgSlug }: Props) => {
  const isMounted = useRef(false);
  const [recentlyVisitedPrograms, setRecentlyVisitedPrograms] = useState<Program[]>([]);

  // Need to wait for CSR, because the programs are stored in local storage
  useEffect(() => {
    if (!isMounted.current) {
      setRecentlyVisitedPrograms(getProgramsFromLocalStorage(orgId));
      isMounted.current = true;
    }

    return () => {
      isMounted.current = false;
    };
  }, [orgId]);

  if (shouldHideProgramsBar || (isMounted.current && !recentlyVisitedPrograms.length)) {
    return null;
  }

  return (
    <Grid container css={styles.wrapper}>
      <div css={{ display: 'flex', flexWrap: 'nowrap' }}>
        <HelpIcon size="large" css={styles.icon} />
        <Typography2 component="h3" variant="subtitleMedium">
          {_t('Recently visited learning programs:')}
        </Typography2>
      </div>
      {isMounted.current ? (
        recentlyVisitedPrograms.slice(0, MAX_VISIBILE_PROGRAMS).map(({ slug, name }) => (
          <Link
            variant="quiet"
            typographyVariant="bodyPrimary"
            key={slug}
            to={`/o/${orgSlug}/admin/programs/${slug}/main`}
            css={styles.link}
            component={ReactRouterLink}
            role="link"
          >
            {name}
          </Link>
        ))
      ) : (
        <>
          <Typography2 component="p" css={styles.link} variant="bodyPrimary">
            <Skeleton width={150} />
          </Typography2>
          <Typography2 component="p" css={styles.link} variant="bodyPrimary">
            <Skeleton width={150} />
          </Typography2>
          <Typography2 component="p" css={styles.link} variant="bodyPrimary">
            <Skeleton width={150} />
          </Typography2>
        </>
      )}
    </Grid>
  );
};

export default RecentlyVisitedLearningProgramsBar;
